import React, { useEffect, useRef, useState, useCallback } from "react";
import Confetti from "react-confetti";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUnseenCelebrationEvents,
  markAllCelebrationsSeen,
} from "../../redux/slices/CelebrationThunks";
import { PartyBalloon, PartyHat } from "../../assets";
import Button from "./Button";

// Theme colors
const COLORS = {
  white: "#FFFFFF",
  navy: "#212543",
  blue: "#6162C5",
  grayText: "#64748B",
};

// Owl mascot images - replace these URLs with actual owl mascot images
const OWL_IMAGES = {
  partyHat: PartyHat, // Owl with party hat (for owner celebrations)
  balloon: PartyBalloon, // Owl with balloon (for community celebrations)
};

// Generate celebration message based on event type, ownership, and display data
const getCelebrationMessage = (event) => {
  const { event_type, is_owner, display_data } = event;
  const memberName = display_data?.member_name || "A member";
  const rewardAmount = display_data?.reward_amount;
  const vendorName = display_data?.vendor_name;

  // First referral - only shown to the owner (triggered from create referral)
  if (event_type === "first_referral") {
    return {
      title: "Congratulations!",
      subtitle: "You've made your first referral!",
      owlType: "partyHat",
      description:
        "This is the start of something great. Keep referring and watch your rewards grow!",
      confettiCount: 300,
    };
  }

  // Won referral - different message for owner vs other users
  if (event_type === "won_referral") {
    if (is_owner) {
      // Owner sees their own success
      const rewardText = rewardAmount ? `You've earned £${rewardAmount}!` : "Your rewards are on the way!";
      return {
        title: "You Won!",
        subtitle: "Your referral was successful!",
        owlType: "partyHat",
        description: `${rewardText}${
          vendorName ? ` Great job closing with ${vendorName}.` : ""
        } Keep up the momentum!`,
        confettiCount: 600,
      };
    } else {
      // Other users see a community celebration
      const rewardText = rewardAmount ? ` and earned £${rewardAmount}` : "";
      return {
        title: "Community Win!",
        subtitle: "A Partner member just won a Referral!",
        owlType: "balloon",
        description: `A Partner member just closed a successful Referral${
          vendorName ? ` with ${vendorName}` : ""
        }${rewardText}. Celebrate with them!`,
        confettiCount: 300,
      };
    }
  }

  // Referral closed - different message for owner vs other users
  if (event_type === "referral_closed") {
    if (is_owner) {
      return {
        title: "Deal Closed!",
        subtitle: "Your referral deal is complete!",
        owlType: "partyHat",
        description: `Great job! Another successful deal${
          vendorName ? ` with ${vendorName}` : ""
        } closed. Your rewards are on the way!`,
        confettiCount: 400,
      };
    } else {
      return {
        title: "Deal Closed!",
        subtitle: `${memberName} closed a deal!`,
        owlType: "balloon",
        description: `${memberName} just closed a deal${
          vendorName ? ` with ${vendorName}` : ""
        }. The community is winning!`,
        confettiCount: 250,
      };
    }
  }

  // Default message for unknown event types
  return {
    title: "Congratulations!",
    subtitle: "Something great happened!",
    owlType: "partyHat",
    description: "Keep up the amazing work!",
    confettiCount: 300,
  };
};

// Celebration sound URL (royalty-free applause/cheer sound)
const CELEBRATION_SOUND_URL =
  "https://assets.mixkit.co/active_storage/sfx/2013/2013-preview.mp3";

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
`;

// Confetti container to prevent overflow
const ConfettiWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  pointer-events: none;
  z-index: 9999;
`;

// Styled components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(33, 37, 67, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9998;
  backdrop-filter: blur(4px);
  padding: 16px;
  box-sizing: border-box;
`;

const CelebrationCard = styled.div`
  background: ${COLORS.white};
  border-radius: 16px;
  padding: 32px 24px;
  max-width: 540px;
  width: 100%;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: ${fadeIn} 0.3s ease-out;
  position: relative;
  font-family: Montserrat, sans-serif;
  @media (min-width: 480px) {
    padding: 40px 32px;
  }
`;
const ButtonDiv = styled.div`
  width: fit-content;
  align-self: center;
  margin: 0 auto;
`;

const CloseIconButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: ${COLORS.navy};
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
  opacity: 0.8;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 12px;
    height: 12px;
    color: ${COLORS.white};
  }
`;

const MascotImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
  margin-bottom: 16px;
  animation: ${float} 3s ease-in-out infinite;

  @media (min-width: 480px) {
    width: 140px;
    height: 140px;
  }
`;

const Title = styled.h2`
  color: var(--Color-Text-Text, #212543);
  text-align: center;

  /* H6 */
  font-family: Montserrat;
  font-size: var(--Fonts-H6, 24px);
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  letter-spacing: -0.24px;
  margin: 0 0 16px 0;

  @media (min-width: 480px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.h3`
  color: var(--Color-Text-Text, #212543);
  text-align: center;

  /* P2 */
  font-family: Montserrat;
  font-size: var(--Fonts-P2, 18px);
  font-style: normal;
  font-weight: 500;
  line-height: 160%; /* 28.8px */
  margin: 0 0 16px 0;

  @media (min-width: 480px) {
    font-size: 16px;
    margin: 0 0 16px 0;
  }
`;

const Description = styled.p`
  color: var(--Color-Text-Text, #212543);
  text-align: center;

  /* P4 */
  font-family: Montserrat;
  font-size: var(--Fonts-P4, 14px);
  font-style: normal;
  font-weight: 500;
  line-height: 160%; /* 22.4px */
  margin: 0 0 16px 0;
  padding: 0 8px;

  @media (min-width: 480px) {
    font-size: 14px;
    margin: 0 0 24px 0;
  }
`;

const ViewDetailsButton = styled.button`
  background: ${COLORS.navy};
  color: ${COLORS.white};
  border: none;
  border-radius: 100%;
  padding: 12px 32px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
  width: 100%;
  max-width: 200px;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`;

// Toast animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Toast container for community celebrations (bottom-left)
const ToastContainer = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9998;
  animation: ${slideIn} 0.3s ease-out forwards;
`;

const ToastCard = styled.div`
  background: ${COLORS.white};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2);
  max-width: 360px;
  font-family: Montserrat, sans-serif;
`;

const ToastImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  flex-shrink: 0;
`;

const ToastContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ToastTitle = styled.h4`
  color: ${COLORS.navy};
  font-family: Montserrat;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 4px 0;
`;

const ToastMessage = styled.p`
  color: ${COLORS.grayText};
  font-family: Montserrat;
  font-size: 12px;
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
`;

const ToastCloseButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  transition: opacity 0.2s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${COLORS.navy};
  }
`;

// Custom hook to get window dimensions
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
};

// Set to true to test with mock event, set to false for production
const TEST_MODE = false;

const MOCK_EVENT = {
  _id: "test_123",
  event_type: "won_referral", // Try: "won_referral", "referral_closed"
  is_owner: false, // Try: true for owner view, false for community view
  display_data: {
    member_name: "John D.",
    reward_amount: 500,
    vendor_name: "Acme Corp",
  },
};

export default function CelebrationConfetti() {
  const dispatch = useDispatch();
  const { events: reduxEvents } = useSelector((state) => state.celebration);
  const { user } = useSelector((state) => state.auth);
  const { width, height } = useWindowSize();

  // Use mock event in test mode, otherwise use redux events
  const events = TEST_MODE ? [MOCK_EVENT] : reduxEvents;

  const [activeEvent, setActiveEvent] = useState(null);
  const [playConfetti, setPlayConfetti] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const hasFetchedRef = useRef(false);
  const hasCompletedRef = useRef(false);
  const audioRef = useRef(null);
  const autoDismissRef = useRef(null);

  // Don't show confetti on signup or otp screens
  const excludedPaths = ["/signup", "/otp"];
  const isExcludedRoute = excludedPaths.some((path) =>
    window.location.pathname.startsWith(path)
  );

  // Play celebration sound
  const playSound = useCallback(() => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      audioRef.current = new Audio(CELEBRATION_SOUND_URL);
      audioRef.current.volume = 0.5;
      audioRef.current.play().catch(() => {
        // Silently fail if autoplay is blocked
      });
    } catch (e) {
      // Silently fail if audio fails
    }
  }, []);

  // Stop sound
  const stopSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  // Handle dismiss - marks ALL events as seen to avoid multiple popups
  const handleDismiss = useCallback(() => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;

    // Clear auto-dismiss timer
    if (autoDismissRef.current) {
      clearTimeout(autoDismissRef.current);
      autoDismissRef.current = null;
    }

    setPlayConfetti(false);
    setShowCard(false);
    setShowToast(false);
    stopSound();

    // Mark all events as seen to prevent multiple popups (user-friendly)
    // Skip API call in test mode
    if (!TEST_MODE) {
      dispatch(markAllCelebrationsSeen());
    }
    setActiveEvent(null);
  }, [dispatch, stopSound]);

  // 1️⃣ Fetch unseen events when user logs in (skip in test mode)
  useEffect(() => {
    if (TEST_MODE) return;
    if (!user) return; // Don't fetch if not logged in
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    dispatch(fetchUnseenCelebrationEvents());
  }, [dispatch, user]);

  // 2️⃣ Start confetti when event is ready - prioritize owner events
  useEffect(() => {
    // Don't trigger if already dismissed (prevents double popup during async API call)
    if (hasCompletedRef.current) return;

    if (!activeEvent && events.length) {
      // Prioritize owner events (their own wins) over community events
      const ownerEvent = events.find((e) => e.is_owner);
      const event = ownerEvent || events[0];
      setActiveEvent(event);
      setPlayConfetti(true);

      // Show popup for owner events, toast for community events
      if (event.is_owner) {
        setShowCard(true);
      } else {
        setShowToast(true);
      }

      // Small delay before playing sound for better UX
      setTimeout(() => {
        playSound();
      }, 300);
    }
  }, [events, activeEvent, playSound]);

  // 3️⃣ Auto-dismiss after 6 seconds
  useEffect(() => {
    if (showCard || showToast) {
      autoDismissRef.current = setTimeout(() => {
        handleDismiss();
      }, 6000);

      return () => {
        if (autoDismissRef.current) {
          clearTimeout(autoDismissRef.current);
        }
      };
    }
  }, [showCard, showToast, handleDismiss]);

  // 4️⃣ Reset completion ref when celebration cycle ends, allowing new celebrations
  useEffect(() => {
    if (!activeEvent && events.length === 0) {
      hasCompletedRef.current = false;
    }
  }, [activeEvent, events.length]);

  // Cleanup audio and timer on unmount
  useEffect(() => {
    return () => {
      stopSound();
      if (autoDismissRef.current) {
        clearTimeout(autoDismissRef.current);
      }
    };
  }, [stopSound]);

  // Don't render on excluded routes or if no active event
  if (isExcludedRoute || !activeEvent) return null;

  const messageData = getCelebrationMessage(activeEvent);

  // X icon SVG component
  const CloseIcon = () => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );

  return (
    <>
      {/* Confetti layer - wrapped to prevent overflow */}
      {playConfetti && (
        <ConfettiWrapper>
          <Confetti
            width={width}
            height={height}
            recycle={false}
            numberOfPieces={messageData.confettiCount}
          />
        </ConfettiWrapper>
      )}

      {/* Celebration card overlay - for owner events */}
      {showCard && (
        <Overlay onClick={handleDismiss}>
          <CelebrationCard onClick={(e) => e.stopPropagation()}>
            <CloseIconButton onClick={handleDismiss} aria-label="Close">
              <CloseIcon />
            </CloseIconButton>

            <MascotImage
              src={OWL_IMAGES[messageData.owlType]}
              alt="Celebration mascot"
            />
            <Title>{messageData.title}</Title>
            <Subtitle>{messageData.subtitle}</Subtitle>
            <Description>{messageData.description}</Description>
            <ButtonDiv>
              <Button variant="black" onClick={handleDismiss}>
                View Details
              </Button>
            </ButtonDiv>
          </CelebrationCard>
        </Overlay>
      )}

      {/* Toast notification - for community events (non-blocking) */}
      {showToast && (
        <ToastContainer>
          <ToastCard>
            <ToastImage
              src={OWL_IMAGES[messageData.owlType]}
              alt="Celebration"
            />
            <ToastContent>
              <ToastTitle>{messageData.title}</ToastTitle>
              <ToastMessage>{messageData.subtitle}</ToastMessage>
            </ToastContent>
            <ToastCloseButton onClick={handleDismiss} aria-label="Close">
              <CloseIcon />
            </ToastCloseButton>
          </ToastCard>
        </ToastContainer>
      )}
    </>
  );
}
